import { supabase } from '../lib/supabase.js';
import { getState } from '../state/store.js';
import { parsePDFStatement, parseCSVOrText, parseXLSXStatement } from './parser.js';
import { isOnline, OFFLINE_SAVE_MESSAGE } from '../lib/onlineGuard.js';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export async function parseFile(file) {
  const name = file.name.toLowerCase();
  if (name.endsWith('.pdf')) {
    return parsePDFStatement(file);
  }
  if (name.endsWith('.csv') || name.endsWith('.txt')) {
    const text = await file.text();
    return parseCSVOrText(text);
  }
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
    return parseXLSXStatement(file);
  }
  throw new Error('Unsupported file type. Upload a PDF, CSV, or XLSX statement.');
}

// Returns a list of reasons; empty list = valid.
export function validateRow(row) {
  const reasons = [];
  if (!row.entry_date || !DATE_RE.test(row.entry_date)) reasons.push('Invalid date');
  const amt = Number(row.amount);
  if (!amt || Number.isNaN(amt) || amt <= 0) reasons.push('Invalid amount');
  if (row.type !== 'income' && row.type !== 'expense') reasons.push('Invalid type');
  if (!row.description || !String(row.description).trim()) reasons.push('Missing description');
  return reasons;
}

// Matches an existing transaction on site_id (implicit -- existingTransactions
// is already scoped to the selected site) + entry_date + type + amount + description.
export function findDuplicate(row, existingTransactions) {
  const desc = String(row.description || '').trim().toLowerCase();
  return existingTransactions.some((t) =>
    t.entry_date === row.entry_date &&
    t.type === row.type &&
    Number(t.amount) === Number(row.amount) &&
    String(t.description || '').trim().toLowerCase() === desc
  );
}

/**
 * Classifies every parsed row against the currently loaded site transactions.
 * Returns rows annotated with { status: 'valid'|'invalid'|'duplicate', reasons }.
 */
export function classifyRows(rows) {
  const state = getState();
  const existing = state.recentTransactions;
  return rows.map((row) => {
    const reasons = validateRow(row);
    if (reasons.length) return { ...row, status: 'invalid', reasons };
    if (findDuplicate(row, existing)) return { ...row, status: 'duplicate', reasons: [] };
    return { ...row, status: 'valid', reasons: [] };
  });
}

/**
 * Inserts the given rows (already filtered to valid + user-confirmed duplicates)
 * into the EXISTING public.transactions table via a single bulk insert -- one
 * Postgres statement, so it's all-or-nothing: if any row fails, none are written
 * and the caller sees the real Supabase error, never a false "success".
 */
export async function importRows(rows, siteId, fileName) {
  if (!isOnline()) return { ok: false, error: OFFLINE_SAVE_MESSAGE };
  if (!siteId) return { ok: false, error: 'No site selected' };
  if (!rows.length) return { ok: false, error: 'No rows selected to import' };

  const { data: { session } } = await supabase.auth.getSession();
  const userId = session?.user?.id;
  if (!userId) return { ok: false, error: 'Not signed in' };

  const payload = rows.map((r) => ({
    site_id: siteId,
    entry_date: r.entry_date,
    type: r.type,
    category: r.category || (r.type === 'income' ? 'Income' : 'Other'),
    description: r.description,
    amount: Number(r.amount),
    payment_mode: r.payment_mode || 'Imported',
    notes: r.notes || null,
    created_by: userId
  }));

  const { error } = await supabase.from('transactions').insert(payload);
  if (error) {
    await recordUploadAudit(fileName, 'failed', 0, userId);
    return { ok: false, error: error.message };
  }

  await recordUploadAudit(fileName, 'imported', payload.length, userId);
  return { ok: true, count: payload.length };
}

// Uses the EXISTING public.statement_uploads table as-is -- no schema change.
// A failure here must not block the import itself (the transactions are already
// committed); it's just an audit trail.
async function recordUploadAudit(fileName, status, importedCount, userId) {
  const { error } = await supabase.from('statement_uploads').insert({
    file_name: fileName,
    status,
    imported_count: importedCount,
    uploaded_by: userId
  });
  if (error) console.error('Failed to record statement_uploads audit row', error);
}
